import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InventoryIcon from '@mui/icons-material/Inventory';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import UpdateIcon from '@mui/icons-material/Update';
import axios from 'axios';
import config from '../config';
import MirakelParticles from '../components/MirakelParticles';

interface DashboardStats {
  products: {
    total: number;
    brands: number;
    categories: number;
    needingDescription: number;
  };
  prices: {
    productsWithPrice: number;
    averagePrice: number;
    onSale: number;
  };
}

interface Import {
  type: 'import';
  name: string;
  status: string;
  timestamp: string;
  rowCount: number;
}

interface ActivitiesResponse {
  imports: Import[];
}

interface ActivityItem {
  type: 'price' | 'product' | 'description';
  message: string;
  timestamp: string;
}

interface MetricCardProps {
  title: string;
  value: string;
  helper: string;
  icon: React.ReactNode;
  accent: string;
  progressLabel: string;
  progressValue: number;
  progressText: string;
  footerItems: { label: string; value: string | number }[];
  actionLabel: string;
  onAction: () => void;
}

const safePercent = (value: number, total: number) => {
  if (!total || total <= 0) {
    return 0;
  }

  return Math.min(100, Math.max(0, (value / total) * 100));
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  helper,
  icon,
  accent,
  progressLabel,
  progressValue,
  progressText,
  footerItems,
  actionLabel,
  onAction,
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: { xs: 2.5, md: 3 }, height: '100%' }}>
      <Stack spacing={2.5} sx={{ height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: alpha(accent, 0.12),
              color: accent,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {helper}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="h2" sx={{ color: accent }}>
            {value}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {progressLabel}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {progressText}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                '& .MuiLinearProgress-bar': {
                  backgroundColor: accent,
                },
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${footerItems.length}, minmax(0, 1fr))`,
            gap: 1,
            pt: 0.5,
          }}
        >
          {footerItems.map((item) => (
            <Box
              key={item.label}
              sx={{
                borderRadius: 1,
                bgcolor: alpha(accent, 0.06),
                p: 1.25,
              }}
            >
              <Typography variant="subtitle1" sx={{ color: accent }}>
                {item.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 'auto' }}>
          <Button
            variant="text"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            onClick={onAction}
            sx={{ px: 0 }}
          >
            {actionLabel}
          </Button>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    products: {
      total: 0,
      brands: 0,
      categories: 0,
      needingDescription: 0,
    },
    prices: {
      productsWithPrice: 0,
      averagePrice: 0,
      onSale: 0,
    },
  });

  const [recentActivities, setRecentActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        const [statsResponse, activitiesResponse] = await Promise.all([
          axios.get<DashboardStats>(`${config.API_URL}/dashboard/stats`),
          axios.get<ActivitiesResponse>(
            `${config.API_URL}/dashboard/activities`,
          ),
        ]);

        setStats(statsResponse.data);

        const allActivities = activitiesResponse.data.imports.map((imp) => ({
          type: 'product' as const,
          message: `Produktimport: ${imp.name} (${imp.rowCount} rader)`,
          timestamp: imp.timestamp,
        }));

        setRecentActivities(allActivities);
      } catch (error) {
        console.error('Fel vid hämtning av dashboard-data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const descriptionPercent = safePercent(
    stats.products.needingDescription,
    stats.products.total,
  );
  const salePercent = safePercent(
    stats.prices.onSale,
    stats.prices.productsWithPrice,
  );

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          py: { xs: 0.5, md: 1 },
          px: { xs: 0, md: 1 },
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <MirakelParticles
          variant="ambient"
          density="low"
          opacity={0.34}
          sx={{
            display: { xs: 'none', sm: 'block' },
            left: 'auto',
            width: { sm: '58%', md: '46%' },
            maskImage: 'linear-gradient(90deg, transparent, #000 28%)',
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Översikt
          </Typography>
          <Typography variant="h3" component="h1">
            Dashboard
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            color: 'text.secondary',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Produkter totalt
            </Typography>
            <Typography variant="subtitle2">
              {stats.products.total.toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Prisatta artiklar
            </Typography>
            <Typography variant="subtitle2">
              {stats.prices.productsWithPrice.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title="Produkter"
                value={stats.products.total.toLocaleString()}
                helper="Sortiment och innehåll"
                icon={<InventoryIcon />}
                accent="#2563EB"
                progressLabel="Behöver beskrivning"
                progressValue={descriptionPercent}
                progressText={stats.products.needingDescription.toLocaleString()}
                footerItems={[
                  { label: 'Varumärken', value: stats.products.brands },
                  { label: 'Kategorier', value: stats.products.categories },
                ]}
                actionLabel="Hantera produkter"
                onAction={() => navigate('/product-management')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <MetricCard
                title="Priser"
                value={stats.prices.productsWithPrice.toLocaleString()}
                helper="Prisstatus och kampanjer"
                icon={<MonetizationOnIcon />}
                accent="#06B6D4"
                progressLabel="Produkter på rea"
                progressValue={salePercent}
                progressText={stats.prices.onSale.toLocaleString()}
                footerItems={[
                  {
                    label: 'Genomsnittspris',
                    value: `${stats.prices.averagePrice.toLocaleString()} kr`,
                  },
                ]}
                actionLabel="Gå till priser"
                onAction={() => navigate('/price-change')}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: { xs: 2.5, md: 3 },
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: alpha('#F97316', 0.12),
                    color: '#F97316',
                  }}
                >
                  <UpdateIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Senaste aktiviteter</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Importer och uppdateringar
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <List sx={{ py: 0 }}>
                {recentActivities.length > 0 ? (
                  recentActivities.slice(0, 8).map((activity, index) => (
                    <React.Fragment key={`${activity.timestamp}-${index}`}>
                      <ListItem alignItems="flex-start" sx={{ py: 1.75 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              bgcolor:
                                activity.type === 'price'
                                  ? alpha('#06B6D4', 0.12)
                                  : activity.type === 'product'
                                    ? alpha('#2563EB', 0.12)
                                    : alpha('#16A34A', 0.12),
                              color:
                                activity.type === 'price'
                                  ? '#06B6D4'
                                  : activity.type === 'product'
                                    ? '#2563EB'
                                    : '#16A34A',
                            }}
                          >
                            {activity.type === 'price' && (
                              <MonetizationOnIcon fontSize="small" />
                            )}
                            {activity.type === 'product' && (
                              <InventoryIcon fontSize="small" />
                            )}
                            {activity.type === 'description' && (
                              <SmartToyIcon fontSize="small" />
                            )}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.message}
                          secondary={activity.timestamp}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: 650,
                            color: 'text.primary',
                          }}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                          }}
                        />
                      </ListItem>
                      {index < recentActivities.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Inga aktiviteter att visa
                    </Typography>
                  </Box>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
